import BulkFilePreviewModal from '@/components/CommonComponents/BulkFilePreviewModal';
import DropdownWithRadio from '@/components/CommonComponents/RadioButtons';
import { uploadBufferToS3 } from '@/helper/s3.helper';
import { formatTargetAudience } from '@/helper/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { AppDispatch, RootState } from '@/store';
import { getProjectsDropdown } from '@/store/slices/projectsSlice/projectsSlice';
import { getSitesByProjectId } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import {
  getPosterCode,
  getSurveySetBySiteId,
  getSurveySetUsersCount,
} from '@/store/slices/surveySetManagementSlice/surveySetManagementSlice';
import { CreateNotifications, TargetGroup } from '@/types/notification.type';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface TargetAudienceProps {
  messageData: CreateNotifications;
  setMessageData: React.Dispatch<React.SetStateAction<CreateNotifications>>;
  hasDynamicVariables?: boolean;
  lockedProjectId?: number;
  lockedProjectName?: string;
}

interface Project {
  id: number;
  name: string;
}

interface Site {
  id: number;
  name: string;
}

interface SurveySet {
  id: number;
  title: string;
  posterCode?: string;
}

const TargetAudience = forwardRef(
  (
    {
      messageData,
      setMessageData,
      hasDynamicVariables = false,
      lockedProjectId,
      lockedProjectName,
    }: TargetAudienceProps,
    ref,
  ) => {
    const dispatch = useDispatch<AppDispatch>();
    const options: { id: TargetGroup; title: string; desc: string }[] = [
      { id: 'ALL_USERS', title: 'All Users', desc: 'Send to all users on this platform' },
      { id: 'FILTER_USERS', title: 'Filter by Criteria', desc: 'Target specific segments' },
      { id: 'BULK_USERS', title: 'Bulk Upload', desc: 'Upload a list of specific users to target' },
    ];
    const [file, setFile] = useState<File | null>(null);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [errors, setErrors] = useState({
      project: '',
      site: '',
      survey: '',
      file: '',
      posterCode: '',
    });
    const debouncedPosterCode = useDebounce(messageData.posterCode, 500);

    const resetTargetFields = () => {
      // setProjectId(null);
      // setSiteId(null);
      // setPosterCode('');
      // setFile(null);
      // setSelectedProject('Choose project');
      // setSelectedSite('Choose Site');
      // setSelectedSurveySet('Choose survey-set');
      // setMessageData((prev) => ({
      //   ...prev,
      //   projectId: null,
      //   siteId: null,
      //   surveySetId: undefined,
      //   posterCode: '',
      //   fileUrl: '',
      //   fileData: null,
      // }));
    };

    const projectsDropdown = useSelector(
      (state: RootState) => state?.projects?.list?.projectsDropdown || [],
    );

    const projectOptions: string[] = [
      'Choose project',
      ...projectsDropdown.map((project: Project) => project.name),
    ];
    const projectsObject: Project[] = [{ id: 0, name: 'Choose project' }, ...projectsDropdown];

    const { sitesByProject } = useSelector((state: RootState) => state.sites);
    const siteOptions: string[] = [
      'Choose Site',
      ...(sitesByProject?.map((site: Site) => site.name) ?? []),
    ];
    const sitesObject: Site[] = [{ id: 0, name: 'Choose Site' }, ...(sitesByProject || [])];

    const surveySetArray = useSelector((state: RootState) => state.surveySet.surveysetsBySite.data);
    const surveyOptions: string[] = [
      'Choose survey-set',
      ...surveySetArray.map((set) => set.title),
    ];
    const surveySetObject: (SurveySet & { id: number; title: string })[] = [
      { id: 0, title: 'Choose survey-set' },
      ...surveySetArray,
    ];

    const { estimatedCount, loadingEstimatedCount } = useSelector(
      (state: RootState) => state.surveySet,
    );

    const { posterCodeData } = useSelector((state: RootState) => state.surveySet);

    useEffect(() => {
      if (!posterCodeData?.data) return;
      // const projectId = survey?.site?.project?.id;
      // const siteId = survey?.site?.id;
      // const surveySetId = survey?.id;
      setMessageData((prev) => ({
        ...prev,
        projectId: posterCodeData?.data.site?.project?.id,
        siteId: posterCodeData?.data.site?.id,
        surveySetId: posterCodeData?.data.id,
      }));
    }, [posterCodeData]);

    useEffect(() => {
      dispatch(getProjectsDropdown());
    }, [dispatch]);

    useEffect(() => {
      // if (debouncedPosterCode?.length != 6)
      if (debouncedPosterCode && debouncedPosterCode !== '') {
        dispatch(getPosterCode(debouncedPosterCode));
      }
    }, [debouncedPosterCode]);

    const selectedSurveySet =
      surveySetObject.find((s) => s.id === messageData?.surveySetId)?.title ?? 'Choose Surveyset';

    const selectedProject =
      projectsObject.find((p) => p.id === messageData.projectId)?.name ?? 'Choose Project';

    const selectedSite =
      sitesObject.find((s) => s.id === messageData.siteId)?.name ?? 'Choose Site';

    useEffect(() => {
      // Reconstruct file from fileData if it exists
      if (messageData.fileData && !file) {
        const buffer = Uint8Array.from(atob(messageData.fileData.content), (c) => c.charCodeAt(0));
        const reconstructedFile = new File([buffer], messageData.fileData.name, {
          type: messageData.fileData.type,
        });
        setFile(reconstructedFile);
      } else if (!messageData.fileData) {
        setFile(null);
      }
    }, [messageData.projectId, messageData.siteId, messageData.posterCode, messageData.fileData]);

    useEffect(() => {
      dispatch(getProjectsDropdown());
    }, [dispatch]);

    useEffect(() => {
      if (messageData.projectId && messageData.projectId > 0) {
        dispatch(getSitesByProjectId(messageData.projectId));
      }
    }, [dispatch, messageData.projectId]);

    useEffect(() => {
      if (messageData.siteId && messageData.siteId > 0) {
        dispatch(getSurveySetBySiteId(messageData.siteId));
      }
    }, [dispatch, messageData.siteId]);

    useEffect(() => {
      if (messageData.targetGroup !== 'FILTER_USERS') return;
      if (!messageData.projectId) return;

      const filters: any = {};

      if (messageData.projectId) filters.projectId = messageData.projectId;
      if (messageData.siteId) filters.siteId = messageData.siteId;
      if (messageData.surveySetId) filters.surveySetId = messageData.surveySetId;
      if (messageData.posterCode) filters.posterCode = messageData.posterCode;

      dispatch(getSurveySetUsersCount(filters));
    }, [dispatch, messageData]);

    const validateFilterUsers = () => {
      const newErrors: typeof errors = {
        project: '',
        site: '',
        survey: '',
        file: '',
        posterCode: '',
      };
      if (!messageData.projectId || messageData.projectId === 0)
        newErrors.project = 'Please select a project';
      setErrors(newErrors);
      return Object.values(newErrors).every((e) => e === '');
    };

    const validateFile = (file: File) => {
      const validExtensions = ['.xlsx', '.xls', '.xml', '.csv'];
      const fileName = file.name.toLowerCase();
      const hasValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/xml',
        'application/xml',
        'text/csv',
        'application/csv',
      ];

      if (!hasValidExtension || !validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          file: 'Invalid file type. Upload XLSX, XLS, XML or CSV files only.',
        }));
        return false;
      }

      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, file: 'File size exceeds 10MB limit.' }));
        return false;
      }

      if (file.size === 0) {
        setErrors((prev) => ({ ...prev, file: 'File is empty. Please upload a valid file.' }));
        return false;
      }

      setErrors((prev) => ({ ...prev, file: '' }));
      return true;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) {
        setErrors((prev) => ({ ...prev, file: 'Please upload a file' }));
        return;
      }

      if (validateFile(selectedFile)) {
        setFile(selectedFile);

        // Parse file to check for template variables
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = event.target?.result;
          if (data) {
            // Check if file contains required template variables
            const templateVariables = extractTemplateVariables(messageData.message);
            if (templateVariables.length > 0) {
              validateFileColumns(data, templateVariables, selectedFile);
            } else {
              // No template variables, proceed normally
              processFile(selectedFile);
            }
          }
        };

        if (selectedFile.name.toLowerCase().endsWith('.csv')) {
          reader.readAsText(selectedFile);
        } else {
          reader.readAsArrayBuffer(selectedFile);
        }

        setErrors((prev) => ({ ...prev, file: '' }));
      }
    };

    const extractTemplateVariables = (text: string): string[] => {
      const regex = /\{\{\s*(\w+)\s*\}\}/g;
      const variables: string[] = [];
      let match;
      while ((match = regex.exec(text)) !== null) {
        if (!variables.includes(match[1])) {
          variables.push(match[1]);
        }
      }
      return variables;
    };

    const validateFileColumns = (
      data: string | ArrayBuffer,
      templateVariables: string[],
      file: File,
    ) => {
      let headers: string[] = [];

      if (file.name.toLowerCase().endsWith('.csv')) {
        // Parse
        const text = data as string;
        const lines = text.split('\n');
        if (lines.length > 0) {
          headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
        }
      } else {
        // For Excel files, we'll need to use a library like xlsx
        // For now, show a warning that we can't validate Excel columns
        setErrors((prev) => ({
          ...prev,
          file:
            'Excel file uploaded. Please ensure it contains columns: ' +
            templateVariables.join(', '),
        }));
        processFile(file);
        return;
      }

      // Check if all template variables exist as columns
      const missingColumns = templateVariables.filter(
        (variable) => !headers.includes(variable.toLowerCase()),
      );

      if (missingColumns.length > 0) {
        setErrors((prev) => ({
          ...prev,
          file: `Missing required columns: ${missingColumns.join(
            ', ',
          )}. Your file must contain these columns to use template variables.`,
        }));
        setFile(null);
        setMessageData((prev) => ({ ...prev, fileData: null }));
        return;
      }

      // All columns found, proceed with file processing
      processFile(file);
    };

    const processFile = (selectedFile: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = btoa(
          new Uint8Array(reader.result as ArrayBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            '',
          ),
        );

        setMessageData((prev) => ({
          ...prev,
          fileData: {
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
            content: base64,
          },
        }));
      };
      reader.readAsArrayBuffer(selectedFile);
    };

    const handleDownloadTemplate = () => {
      const link = document.createElement('a');
      link.href = '/notification-sample-file.csv';
      link.download = 'notification-sample-file.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const handleProjectChange = (value: string | null) => {
      const foundProject = projectsObject.find((project) => project?.name === value);
      const id = foundProject?.id ?? 0;
      setMessageData((prev) => ({
        ...prev,
        projectId: id > 0 ? id : null,
        siteId: null,
        surveySetId: undefined,
        posterCode: '',
      }));

      if (id > 0) setErrors((prev) => ({ ...prev, project: '' }));
    };

    const handleSiteChange = (value: string | null) => {
      const foundSite = sitesObject.find((site) => site?.name === value);
      const id = foundSite?.id ?? 0;
      setMessageData((prev) => ({
        ...prev,
        siteId: id > 0 ? id : null,
        surveySetId: undefined,
        posterCode: '',
      }));

      if (id > 0) setErrors((prev) => ({ ...prev, site: '' }));
    };

    const handleSurveySetChange = (value: string | null) => {
      const foundSurvey = surveySetObject.find((survey) => survey?.title === value);
      // setSelectedSurveySet(value);

      if (foundSurvey && foundSurvey.id > 0) {
        setMessageData((prev) => ({
          ...prev,
          posterCode: foundSurvey.posterCode || '',
          surveySetId: foundSurvey.id,
        }));
        setErrors((prev) => ({ ...prev, survey: '' }));
      } else {
        setMessageData((prev) => ({
          ...prev,
          posterCode: '',
          surveySetId: undefined,
        }));
      }
    };

    const handlePosterCode = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setMessageData((prev) => ({
        ...prev,
        posterCode: value,
      }));

      if (value?.length === 6) {
        dispatch(getPosterCode(value));
      }
    };

    const validate = () => {
      if (messageData.targetGroup === 'FILTER_USERS') return validateFilterUsers();
      if (messageData.targetGroup === 'BULK_USERS') {
        if (!messageData.fileData && !messageData.fileUrl) {
          setErrors((prev) => ({ ...prev, file: 'Please upload a file' }));
          return false;
        }
        if (messageData.fileData && file) return validateFile(file);
        return true;
      }
      return true;
    };

    const uploadFileToS3 = async () => {
      if (!messageData.fileData) return;

      const buffer = Uint8Array.from(atob(messageData.fileData.content), (c) => c.charCodeAt(0));
      const result = await uploadBufferToS3({
        file: new File([buffer], messageData.fileData.name, { type: messageData.fileData.type }),
        context: 'notification',
        contextId: messageData.title || 'bulk-upload',
      });

      setMessageData((prev) => ({ ...prev, fileUrl: result.url }));
      return result.url;
    };

    useImperativeHandle(ref, () => ({
      validate,
      uploadFileToS3,
    }));

    return (
      <div className="space-y-6">
        <div className="px-7 p-5 border-b border-[#87074C]">
          <h2 className="text-2xl font-bold text-[#621C43]">Target Audience</h2>
          <p className="text-lg font-medium text-[#621C43]">
            Enter the basic details for the notification
          </p>
        </div>

        <div className="">
          <h3 className="font-Lato px-6 pb-2 font-medium text-[#4A082D]">
            Target Audience <span className="text-red-500">*</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-2 px-6">
            {options.map((opt) => {
              const isDisabledByDynamic = hasDynamicVariables && opt.id !== 'BULK_USERS';
              const isDisabledByProject = !!lockedProjectId && opt.id !== 'FILTER_USERS';
              const isDisabled = isDisabledByDynamic || isDisabledByProject;
              const disabledReason = isDisabledByProject
                ? 'Not available when sending from a specific project'
                : isDisabledByDynamic
                ? 'Disabled - Dynamic variables detected'
                : '';
              return (
                <div
                  key={opt.id}
                  onClick={() => {
                    if (isDisabled) return;
                    if (opt.id === 'ALL_USERS') resetTargetFields();
                    setMessageData((prev) => ({
                      ...prev,
                      targetGroup: opt.id,
                    }));
                  }}
                  className={`border rounded-lg p-4 transition ${
                    isDisabled
                      ? 'border-gray-300 bg-white cursor-not-allowed'
                      : messageData.targetGroup === opt.id
                      ? 'border-[#621C43] bg-white cursor-pointer'
                      : 'border-gray-300 bg-white hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border flex items-center justify-center mt-1 ${
                        messageData.targetGroup === opt.id ? 'border-[#621C43]' : 'border-gray-400'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full ${
                          messageData.targetGroup === opt.id ? 'bg-[#621C43]' : 'bg-transparent'
                        }`}
                      ></div>
                    </div>
                    <input
                      type="radio"
                      checked={messageData.targetGroup === opt.id}
                      disabled={isDisabled}
                      onChange={() => {
                        if (isDisabled) return;
                        if (opt.id === 'ALL_USERS') resetTargetFields();
                        setMessageData((prev) => ({ ...prev, targetGroup: opt.id }));
                      }}
                      className="hidden"
                    />

                    <div>
                      <h3 className="font-medium text-[#4A082D]">{opt.title}</h3>
                      {isDisabled ? (
                        <p className="text-xs text-red-500 mt-0.5">{disabledReason}</p>
                      ) : (
                        <p className="text-sm font-light text-[#4A082D]">{opt.desc}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Formatted Target Audience Display */}
        {messageData.targetGroup && (
          <div className="px-6">
            <div className="bg-[#F0F8FF] border border-[#B5D4F5] rounded-xl p-4">
              <h4 className="font-medium text-[#4A082D] mb-2">Selected Target Audience:</h4>
              <p className="text-[#4A082D] text-sm">
                {formatTargetAudience(messageData.targetGroup, {
                  projectName: selectedProject,
                  siteName: selectedSite,
                  surveySetTitle: selectedSurveySet,
                  posterCode: messageData.posterCode,
                  fileName: file?.name || messageData.fileData?.name,
                  estimatedCount: estimatedCount ?? undefined,
                })}
              </p>
            </div>
          </div>
        )}

        {messageData.targetGroup === 'FILTER_USERS' && (
          <div className="space-y-4 mt-4 px-6">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Project Dropdown */}
              <div>
                <DropdownWithRadio
                  key="By Project"
                  label="By Project"
                  options={projectOptions}
                  selected={selectedProject}
                  onChange={handleProjectChange}
                  isOpen={openDropdown === 'By Project'}
                  onOpen={() => setOpenDropdown('By Project')}
                  onClose={() => setOpenDropdown((prev) => (prev === 'By Project' ? null : prev))}
                  placeholder="Choose project"
                  disabled={!!lockedProjectId}
                  className=" flex flex-row relative bg-[#4A082D]/25 text-md rounded-xl"
                />
                {errors.project && <p className="text-red-500 text-sm mt-1">{errors.project}</p>}
              </div>

              {/* Site Dropdown */}
              <div>
                <DropdownWithRadio
                  key="By Site"
                  label="By Site"
                  options={siteOptions}
                  selected={selectedSite}
                  onChange={handleSiteChange}
                  isOpen={openDropdown === 'By Site'}
                  onOpen={() => setOpenDropdown('By Site')}
                  onClose={() => setOpenDropdown((prev) => (prev === 'By Site' ? null : prev))}
                  placeholder="Choose Site"
                  disabled={!messageData.projectId || messageData.projectId === 0}
                  className="flex flex-row relative bg-[#4A082D]/25 text-md rounded-xl"
                />
                {errors.site && <p className="text-red-500 text-sm mt-1">{errors.site}</p>}
              </div>

              {/* Survey-set Dropdown */}
              <div>
                <DropdownWithRadio
                  key="By Survey-set"
                  label="By Survey-set"
                  options={surveyOptions}
                  selected={selectedSurveySet}
                  onChange={handleSurveySetChange}
                  isOpen={openDropdown === 'By Survey-set'}
                  onOpen={() => setOpenDropdown('By Survey-set')}
                  onClose={() =>
                    setOpenDropdown((prev) => (prev === 'By Survey-set' ? null : prev))
                  }
                  placeholder="Choose survey-set"
                  disabled={!messageData.siteId || messageData.siteId === 0}
                  className="flex flex-row relative bg-[#4A082D]/25 text-md rounded-xl"
                />
                {errors.survey && <p className="text-red-500 text-sm mt-1">{errors.survey}</p>}
              </div>

              {/* Poster Code Input */}
              <div className="flex flex-col">
                <input
                  type="number"
                  name="posterCode"
                  value={messageData.posterCode}
                  placeholder="Enter 6-digit poster code"
                  className="border rounded-xl p-1.5 w-full bg-[#4A082D]/20 placeholder-[#4A082D]"
                  onChange={handlePosterCode}
                />
                {errors.posterCode && (
                  <p className="text-red-500 text-sm mt-1">{errors.posterCode}</p>
                )}
              </div>
            </div>

            <div className="bg-[#E8F0FF] border font-Lato border-[#B5C9F5] text-[#4A082D] p-2 rounded-xl">
              <p className="font-medium">
                Estimated Audience:{' '}
                {loadingEstimatedCount
                  ? 'Estimating...'
                  : estimatedCount !== null
                  ? `${estimatedCount} Users`
                  : '—'}
              </p>
              <p className="text-xs text-[#4A082D]">
                This is an approximation based on your filter settings
              </p>
            </div>
          </div>
        )}

        {messageData.targetGroup === 'BULK_USERS' && (
          <div className="space-y-4 px-6">
            <div className="border border-[#D3B4C2] bg-[#F8F4F7] p-2 rounded-lg">
              <p className="font-medium text-[#4A082D] flex items-center gap-2">
                <img
                  src="/Exclamation Triangle2.png"
                  alt="Download Template"
                  className="w-5 h-5 object-contain text-[#4A082D] mt-1"
                />
                Download Template First
              </p>
              <p className="text-sm ml-8 text-[#4A082D] mt-1">
                Use our template to ensure your file has the correct format.
              </p>

              <button
                onClick={handleDownloadTemplate}
                className="mt-3 ml-7 bg-[#621C43] text-white px-4 py-1 rounded-xl hover:bg-[#4E1535] transition flex items-center gap-2"
              >
                Download Excel Template →
              </button>
            </div>

            <div className="border border-gray-300 bg-[#F8F4F7] rounded-lg p-3 text-center">
              <div className="items-center flex justify-center">
                <img
                  src="/upload2.png"
                  alt="Download Template"
                  className="w-7 h-7 object-contain text-[#4A082D] mt-1"
                />
              </div>

              <p className="font-medium text-[#4A082D] text-lg mb-2">Upload Excel File</p>

              <p className="text-sm text-[#4A082D] mb-4">
                Drag and drop your Excel file (.xlsx) or XML (.xml) file here or click to browse
              </p>

              <div className="flex gap-3 justify-center">
                {(file || messageData.fileData) && (
                  <button
                    onClick={() => setShowPreviewModal(true)}
                    className="bg-[#621C43] text-white px-5 py-2 rounded-lg hover:bg-[#4A082D] transition font-medium"
                  >
                    Preview
                  </button>
                )}
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.xml,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="fileInput"
                  />
                  <div className="cursor-pointer bg-[#4C86E7] text-white px-5 py-2 rounded-lg hover:bg-[#4C86E7]/50 transition">
                    Choose File
                  </div>
                </label>
              </div>

              {errors.file && <p className="text-red-500 text-sm mt-2">{errors.file}</p>}

              {(file || messageData.fileData) && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <span className="text-lg">✓</span>
                    <p className="font-medium text-sm">
                      {(() => {
                        const fileName = file?.name || messageData.fileData?.name || '';
                        const fileSize = file?.size || messageData.fileData?.size || 0;
                        const fileType =
                          file?.name.split('.').pop()?.toUpperCase() ||
                          messageData.fileData?.name.split('.').pop()?.toUpperCase() ||
                          '';
                        return `${
                          fileName.length > 25 ? `${fileName.substring(0, 25)}...` : fileName
                        } - Size: ${(fileSize / 1024 / 1024).toFixed(2)} MB | Type: ${fileType}`;
                      })()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="">
              <h3 className="text-[#4A082D] font-medium mb-1">File Requirements</h3>
              <div className="bg-[#F8F4F7] border border-gray-200 rounded-xl p-2">
                <div className="grid grid-cols-2 gap-x- gap-y-2 text-[#4A082D] font-medium text-sm w-max">
                  <div>✓ XLSForm or XML</div>
                  <div>✓ Valid Excel file with user details</div>
                  <div>✓ max. file size 10MB</div>
                  <div>✓ File ID must be unique</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk File Preview Modal */}
        <BulkFilePreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          file={file}
        />
      </div>
    );
  },
);

export default TargetAudience;
